import styled from '@emotion/styled';
import Highcharts, { XAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HCMore from 'highcharts/highcharts-more';
import FunnelModule from 'highcharts/modules/funnel';
import HighchartsPatternFill from 'highcharts/modules/pattern-fill';
import TreeMapModule from 'highcharts/modules/treemap';
import { useAppSelector } from 'modules/Editor/store';
import { selectIsAllFontsLoaded } from 'modules/Editor/store/pageSelector';
import { Ref, memo, useEffect, useRef, useState } from 'react';
import { isTest } from 'utils/environment';
import { HighChartsWidgetTypes } from 'widgets/ChartWidgets/ChartWidget.types';
import { useHighChartOptions } from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommon.helpers';
import { usePreviewControl } from 'widgets/store/PreviewControlProvider';

import RefObject = HighchartsReact.RefObject;

HCMore(Highcharts);
HighchartsPatternFill(Highcharts);
FunnelModule(Highcharts);
TreeMapModule(Highcharts);

const StyledHighChartsWrapper = styled.div`
  position: absolute;

  // prevent click effect on legend
  .highcharts-legend {
    pointer-events: none !important;
  }

  // overwrite pointer events for axis labels to allow for
  // double click to open chart panel
  .highcharts-axis-labels {
    pointer-events: none !important;
  }

  // SVGs must have absolute positioning to render correctly on export
  svg.highcharts-root {
    position: absolute;
  }
`;

interface HighChartsWrapperProps {
  chartType: HighChartsWidgetTypes;
  chartRef: Ref<RefObject>;
  waitForFonts?: boolean;
}

export const HighChartsWrapper = memo(({ chartRef, chartType, waitForFonts = true }: HighChartsWrapperProps) => {
  const previewControl = usePreviewControl();
  const highChartOptions = useHighChartOptions(chartType, { animation: previewControl.chart.animation });
  const isAllFontLoaded = useAppSelector(selectIsAllFontsLoaded);

  /**
   * This workaround is required to prevent text overlapping axis title on font family change. https://github.com/Venngage/editor2/pull/571
   * The first part is for the Editor.
   */
  const labelStyles = (highChartOptions?.xAxis as XAxisOptions)?.labels?.style;
  const previousLabelStyling = useRef((highChartOptions?.xAxis as XAxisOptions)?.labels?.style);
  const [reRender, setReRender] = useState(false);
  const customKey =
    previousLabelStyling.current?.fontFamily !== labelStyles?.fontFamily || reRender
      ? Date.now()
      : highChartOptions.keyCustom;

  useEffect(() => {
    if (previousLabelStyling.current?.fontFamily !== labelStyles?.fontFamily) {
      previousLabelStyling.current = { ...labelStyles };
    }
  }, [labelStyles]);

  // This part of the workaround is specifically for export.
  useEffect(() => {
    if (!isAllFontLoaded && !isTest) return;

    setTimeout(() => {
      setReRender(true);
    }, 250);
  }, [isAllFontLoaded]);

  // highcharts would need to have the font ready to properly calculate the content layout in the chart
  if (waitForFonts && !isAllFontLoaded && !isTest) return null;

  /**
   * Highcharts wrapper must have aria-hidden set to TRUE so that
   * text labels (data, x, y axes) are not picked up in the reading
   * order after export.
   */

  /**
   * This is a workaround to prevent the chart from rendering animation twice when the animation is enabled.
   */
  const previewControlStyle = previewControl.chart?.animation && !reRender ? { display: 'none' } : undefined;

  return (
    <StyledHighChartsWrapper aria-hidden={true} key={customKey} style={previewControlStyle}>
      <HighchartsReact key={customKey} ref={chartRef} highcharts={Highcharts} options={highChartOptions} />
    </StyledHighChartsWrapper>
  );
});

HighChartsWrapper.displayName = 'HighChartsWrapper';
